import React, { useMemo, useState } from 'react';
import type { ClientOrderItem, ClientOrdersStats, OrdersViewMode } from '../types';

interface Props {
  orders: ClientOrderItem[];
  stats: ClientOrdersStats;
  statusFilter: string;
  databaseFilter: string;
  paymentFilter: string;
  typeFilter: string;
  searchQuery: string;
  onStatusFilterChange: (val: string) => void;
  onDatabaseFilterChange: (val: string) => void;
  onPaymentFilterChange: (val: string) => void;
  onTypeFilterChange: (val: string) => void;
  onSearchChange: (val: string) => void;
  onViewOrder: (order: ClientOrderItem) => void;
  onCreateOrderClick: () => void;
  onBack: () => void;
  onClose: () => void;
  onClearDetail?: () => void;
  detailOrder?: ClientOrderItem | null;
  detailSlot?: React.ReactNode;
}

/** Client orders CRM — styles: business-operations.css */
export const ClientOrdersCrmView: React.FC<Props> = ({
  orders,
  stats,
  statusFilter,
  databaseFilter,
  paymentFilter,
  typeFilter,
  searchQuery,
  onStatusFilterChange,
  onDatabaseFilterChange,
  onPaymentFilterChange,
  onTypeFilterChange,
  onSearchChange,
  onViewOrder,
  onCreateOrderClick,
  onBack,
  onClose,
  onClearDetail,
  detailOrder = null,
  detailSlot = null,
}) => {
  const [viewMode, setViewMode] = useState<OrdersViewMode>('cards');

  const statusOptions = useMemo(
    () => ['Все', ...Array.from(new Set(orders.map((o) => o.status).filter(Boolean)))],
    [orders],
  );
  const baseOptions = useMemo(
    () => ['Все', ...Array.from(new Set(orders.map((o) => o.database).filter(Boolean)))],
    [orders],
  );
  const payOptions = useMemo(
    () => ['Все', ...Array.from(new Set(orders.map((o) => o.payment_state).filter(Boolean)))],
    [orders],
  );
  const typeOptions = useMemo(
    () => ['Все', ...Array.from(new Set(orders.map((o) => o.category).filter(Boolean)))],
    [orders],
  );

  const acceptedCount = orders.filter((x) => x.status === 'Принято').length;
  const publishedCount = orders.filter((x) => x.status === 'Опубликовано').length;
  const balanceSum = orders.reduce((sum, x) => sum + Number(x.balance || 0), 0);

  const s = stats || {
    total_matrix: orders.length,
    published_count: publishedCount,
    accepted_count: acceptedCount,
    in_progress_count: orders.filter((x) => x.status === 'В работе').length,
    coauthor_count: orders.filter((x) => x.status === 'Поиск соавторов').length,
    filtered_count: orders.length,
    total_records: orders.length,
    pending_remaining: orders.filter(
      (x) => x.status === 'Ожидает публикации' || x.stage === 'Ожидает публикации',
    ).length,
    total_amount_sum: 0,
    total_paid_sum: 0,
  };

  return (
    <div className="biz-ops-overlay open" role="dialog" aria-modal="true" aria-label="Заказы клиентов">
      <div className="biz-ops-top">
        <button
          type="button"
          className="biz-ops-btn"
          onClick={() => {
            if (detailOrder) onClearDetail?.();
            else onBack();
          }}
        >
          ← Назад
        </button>
        <div>
          <h2>{detailOrder ? `Заказ № ${detailOrder.order_number}` : 'Заказы клиентов'}</h2>
          <p>
            {detailOrder
              ? detailOrder.article_title
              : 'CRM статей, финансов, журналов, этапов и общей истории'}
          </p>
        </div>
        <div className="biz-ops-top-actions">
          {!detailOrder ? (
            <button type="button" className="biz-ops-btn gold" onClick={onCreateOrderClick}>
              + Новый заказ
            </button>
          ) : null}
          <button type="button" className="biz-ops-btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>

      <main className="biz-ops-main">
        {detailOrder && detailSlot ? (
          detailSlot
        ) : (
          <div className="biz-ops-wrap">
            <div className="biz-ops-crumb">
              Бизнес-операции / <b>Заказы клиентов</b>
            </div>

            <div className="biz-ops-panel" style={{ borderColor: 'rgba(158,128,255,.3)' }}>
              <h3>Источник 05 · Общая аналитика реестра</h3>
              <p>
                <b style={{ color: '#fff' }}>{s.total_matrix}</b> статей в итоговой матрице ·{' '}
                <b style={{ color: '#fff' }}>{s.published_count}</b> опубликовано ·{' '}
                <b style={{ color: '#fff' }}>{s.accepted_count}</b> принято ·{' '}
                <b style={{ color: '#fff' }}>{s.in_progress_count}</b> в работе ·{' '}
                <b style={{ color: '#fff' }}>{s.coauthor_count}</b> поиск соавторов ·{' '}
                <b style={{ color: '#fff' }}>{s.pending_remaining}</b> ожидают. Ниже — строки
                реестра по текущим фильтрам.
              </p>
            </div>

            <div className="biz-ops-kpis">
              <div className="biz-ops-kpi">
                <b>{s.total_records || orders.length}</b>
                <span>всего записей</span>
              </div>
              <div className="biz-ops-kpi">
                <b>{s.filtered_count ?? orders.length}</b>
                <span>после фильтра</span>
              </div>
              <div className="biz-ops-kpi">
                <b>{acceptedCount || s.accepted_count}</b>
                <span>принято</span>
              </div>
              <div className="biz-ops-kpi">
                <b>{publishedCount || s.published_count}</b>
                <span>опубликовано</span>
              </div>
              <div className="biz-ops-kpi">
                <b>{balanceSum.toLocaleString('ru-RU')}</b>
                <span>остаток</span>
              </div>
            </div>

            <div className="biz-ops-toolbar">
              <input
                className="biz-ops-input"
                placeholder="Поиск: статья / журнал / автор / №"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              <select
                className="biz-ops-select"
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                aria-label="Фильтр по статусу"
              >
                {statusOptions.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
              <select
                className="biz-ops-select"
                value={databaseFilter}
                onChange={(e) => onDatabaseFilterChange(e.target.value)}
                aria-label="Фильтр по базе"
              >
                {baseOptions.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
              <select
                className="biz-ops-select"
                value={paymentFilter}
                onChange={(e) => onPaymentFilterChange(e.target.value)}
                aria-label="Фильтр по оплате"
              >
                {payOptions.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
              <select
                className="biz-ops-select"
                value={typeFilter}
                onChange={(e) => onTypeFilterChange(e.target.value)}
                aria-label="Фильтр по типу"
              >
                {typeOptions.map((x) => (
                  <option key={x} value={x}>
                    {x === 'Все' ? 'Тип: все' : x}
                  </option>
                ))}
              </select>
              <span className="biz-ops-spacer" />
              <button
                type="button"
                className={`biz-ops-btn${viewMode === 'cards' ? ' gold' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                Карточки
              </button>
              <button
                type="button"
                className={`biz-ops-btn${viewMode === 'table' ? ' gold' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Таблица
              </button>
            </div>

            {viewMode === 'cards' ? (
              <div className="biz-ops-orders">
                {orders.map((order) => (
                  <article key={order.id} className="biz-ops-order">
                    <div className="biz-ops-order-head">
                      <div className="biz-ops-order-no">{order.order_number}</div>
                      <div>
                        <h3>{order.article_title}</h3>
                        <p>
                          {order.journal}
                          {order.category ? ` · ${order.category}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="biz-ops-tags">
                      <span className="biz-ops-chip ok">{order.status}</span>
                      <span className="biz-ops-chip">{order.stage}</span>
                      <span className="biz-ops-chip violet">{order.database}</span>
                      {(order.quartile || order.percentile) && (
                        <span className="biz-ops-chip gold">
                          {order.quartile || '—'}
                          {order.percentile ? ` · ${order.percentile}` : ''}
                        </span>
                      )}
                      <span
                        className={`biz-ops-chip ${order.payment_state === 'Оплачено' ? 'ok' : 'red'}`}
                      >
                        {order.payment_state}
                      </span>
                    </div>
                    <div className="biz-ops-order-foot">
                      <small>Обновлений: {order.update_count}</small>
                      <button type="button" className="biz-ops-btn" onClick={() => onViewOrder(order)}>
                        Открыть карточку →
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="biz-ops-table-wrap">
                <table className="biz-ops-table">
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Статус</th>
                      <th>Этап</th>
                      <th>База</th>
                      <th>Q / %</th>
                      <th>Журнал</th>
                      <th>Статья</th>
                      <th>Авторы</th>
                      <th>Публикация</th>
                      <th>Оплачено</th>
                      <th>Остаток</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.order_number}</td>
                        <td>{order.status}</td>
                        <td>{order.stage}</td>
                        <td>{order.database}</td>
                        <td>
                          {order.quartile || '—'} / {order.percentile || '—'}
                        </td>
                        <td>{order.journal}</td>
                        <td>{order.article_title}</td>
                        <td>{order.authors_json || order.client_name || '—'}</td>
                        <td>{order.target_date || '—'}</td>
                        <td>{Number(order.paid_amount || 0).toLocaleString('ru-RU')}</td>
                        <td>{Number(order.balance || 0).toLocaleString('ru-RU')}</td>
                        <td>
                          <button type="button" className="biz-ops-btn" onClick={() => onViewOrder(order)}>
                            Открыть
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!orders.length ? (
              <div className="biz-ops-panel">
                <h3>Нет записей</h3>
                <p>Заказов по заданным критериям фильтрации не найдено.</p>
              </div>
            ) : null}

            <div className="biz-ops-source">
              <b>Источник 05:</b> фактические поля включают статус, этап, база/категория, квартиль,
              процентиль, журнал/направление, статью, авторов, библиографию, дату публикации, суммы,
              оплаты, остаток, статус оплаты, папку и примечание.
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
